import countryData from "country-list";
import { getGeocode } from "use-places-autocomplete";
import getCountryISO3 from "country-iso-2-to-3";

type AddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

type FormattedAddress = {
  unit?: string;
  streetNumber?: string;
  street?: string;
  suburb?: string;
  state?: string;
  postCode?: string;
  country?: string;
};

const componentMapping: Record<
  string,
  { label: keyof FormattedAddress; field: "long_name" | "short_name" }
> = {
  subpremise: { label: "unit", field: "long_name" },
  street_number: { label: "streetNumber", field: "long_name" },
  route: { label: "street", field: "long_name" },
  locality: { label: "suburb", field: "long_name" },
  administrative_area_level_1: { label: "state", field: "short_name" },
  postal_code: { label: "postCode", field: "long_name" },
  country: { label: "country", field: "short_name" },
};

const countryCodeList = countryData.getCodeList();

const getAllCountries = (isISO3 = false) => {
  return Object.entries(countryCodeList)
    .map(([code, name]) => {
      const upper = code.toUpperCase();
      return {
        label: name,
        value: isISO3 ? (getCountryISO3(upper) ?? upper) : upper,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

const parseGoogleAddressComponents = (
  components: AddressComponent[],
): FormattedAddress => {
  const formattedAddress = components.reduce<FormattedAddress>(
    (address, component) => {
      component.types.forEach((type: string) => {
        const currentMapping = componentMapping[type];
        if (!currentMapping) {
          return;
        }
        address[currentMapping.label] = component[currentMapping.field];
      });
      return address;
    },
    {},
  );

  if (formattedAddress.country) {
    const matchedCountry = getAllCountries().find(
      (country) =>
        country.value.toUpperCase() === formattedAddress.country?.toUpperCase(),
    );
    if (matchedCountry) {
      formattedAddress.country = matchedCountry.value.toUpperCase();
    }
  }

  return formattedAddress;
};

const getAddressFromPlaceId = async (option: {
  value: string;
  label: string;
}): Promise<FormattedAddress> => {
  const placeId = option.value;
  const geocodedPlaces = await getGeocode({ placeId });

  if (geocodedPlaces && geocodedPlaces.length > 0) {
    const parsed = parseGoogleAddressComponents(
      geocodedPlaces[0].address_components || [],
    );

    // Extract leading unit and street number from the label if we have a street name
    const match =
      parsed.street &&
      option.label.match(/^(?:unit\s*|u\s*)?((?=.*\d)[a-z\d\/\-]+)/i);

    if (match) {
      const unitAndStreetNumber = match[1]; // e.g. "1a/30", "406/30", "14"

      if (unitAndStreetNumber.includes("/")) {
        const [unit, street] = unitAndStreetNumber.split("/");
        parsed.unit = unit;
        parsed.streetNumber = street;
      } else {
        parsed.streetNumber = unitAndStreetNumber;
      }
    }

    return parsed;
  }
  return {};
};

export { getAllCountries, parseGoogleAddressComponents, getAddressFromPlaceId };
export type { FormattedAddress };
